import React from 'react';
import styled from 'styled-components';

import {
  Grid,
  Paper,
  TablePagination,
  TablePaginationProps,
  TableContainer as MuiTableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';

import { LinearBuffer } from 'components/Progress';
import { ErrorInterface, FilterProps, FilterType } from 'types';
import TableHead, { TableHeadPropsType } from './TableHead';
import TableToolbar from './TableToolbar';
import TableLoadingState from './TableLoadingState';

const noop = () => {};

type TableProps = {
  renderTable?: () => JSX.Element;
  error?: ErrorInterface;
  isLoading: boolean;
  tableMaxHeight: string;

  /** Table toolbar props
   * Refer to Filters.tsx
   */
  filtersToShow?: FilterProps;
  tableTitle?: string;
  renderToolbar?: () => JSX.Element;
  hideToolbar?: boolean;

  /** Main table props */
  useDefaultTable?: boolean;
  filter?: FilterType;
  headCells?: Array<TableHeadPropsType>;
  onRequestSort?: (event: any, property: string) => void;
  renderRows?: () => JSX.Element;
  isAllCollapse?: boolean;
  useCollapseAll?: boolean;

  /** Table pagination props */
  hidePagination?: boolean;
  rowsPerPageOptions?: Array<number>;
  component?: string;
  count?: number;
  rowsPerPage?: number;
  page?: number;
  onPageChange?: (event: object, page: number) => void;
  onRowsPerPageChange?: (event: object) => void; // Not required
  renderOptions?: () => JSX.Element;
  renderCustomPagination?: () => JSX.Element;
};

function EnhancedTable({
  /** Table toolbar props */
  filtersToShow,
  tableTitle = 'Table Title Here',
  renderToolbar,
  renderOptions,
  hideToolbar = false,
  tableMaxHeight = '1000px',

  renderTable, // Custom Table
  /** Main Table props, if useDefaultTable = true */
  useDefaultTable = false,
  filter,
  headCells = [],
  renderRows = () => <></>,
  onRequestSort = noop,

  useCollapseAll = false,
  isAllCollapse = false,

  /** Table pagination props */
  hidePagination = false,
  rowsPerPageOptions,
  component = 'div',
  count,
  rowsPerPage,
  page,
  onPageChange,
  onRowsPerPageChange, // Not required
  renderCustomPagination,

  /** Error and loading state */
  error,
  isLoading,
}: TableProps) {
  return (
    <Wrapper className="table-wrapper">
      <Paper
        elevation={0}
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.85)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(24px)',
          border: (theme) =>
            theme.palette.mode === 'dark'
              ? '1px solid rgba(148, 163, 184, 0.12)'
              : '1px solid rgba(226, 232, 240, 0.8)',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? '0 4px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) inset, 0 0 20px rgba(59, 130, 246, 0.15)'
              : '0 4px 24px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04), 0 0 20px rgba(59, 130, 246, 0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? '0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.08) inset, 0 0 24px rgba(59, 130, 246, 0.2)'
                : '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04), 0 0 24px rgba(59, 130, 246, 0.12)',
          },
        }}
      >
        {/* Table Toolbar */}
        {renderToolbar ? (
          renderToolbar()
        ) : hideToolbar ? null : (
          <TableToolbar
            renderOptions={renderOptions}
            filtersToShow={filtersToShow}
            tableTitle={tableTitle}
          />
        )}
        {/* Main Table */}
        {useDefaultTable ? (
          <MuiTableContainer
            sx={{
              height: 'auto',
              minHeight: '400px',
              maxHeight: tableMaxHeight,
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '12px',
                height: '12px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(15, 23, 42, 0.3)'
                    : 'rgba(241, 245, 249, 0.5)',
                borderRadius: '8px',
                margin: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(148, 163, 184, 0.3)'
                    : 'rgba(148, 163, 184, 0.4)',
                borderRadius: '8px',
                border: (theme) =>
                  theme.palette.mode === 'dark'
                    ? '2px solid rgba(15, 23, 42, 0.3)'
                    : '2px solid rgba(241, 245, 249, 0.5)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(148, 163, 184, 0.5)'
                      : 'rgba(100, 116, 139, 0.6)',
                },
              },
            }}
          >
            <Table
              stickyHeader
              className="default-table"
              sx={{
                '& .MuiTableHead-root': {
                  '& .MuiTableCell-root': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(15, 23, 42, 0.98)'
                        : 'rgba(248, 250, 252, 0.98)',
                    backdropFilter: 'blur(12px)',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    color: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(203, 213, 225, 0.9)'
                        : 'rgba(51, 65, 85, 0.9)',
                    borderBottom: (theme) =>
                      theme.palette.mode === 'dark'
                        ? '2px solid rgba(148, 163, 184, 0.15)'
                        : '2px solid rgba(226, 232, 240, 0.8)',
                    py: 2,
                    px: 3,
                    whiteSpace: 'nowrap',
                    position: 'relative',
                    '&:first-of-type': {
                      pl: 4,
                    },
                    '&:last-of-type': {
                      pr: 4,
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '1px',
                      background: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'linear-gradient(90deg, transparent 0%, rgba(148, 163, 184, 0.2) 50%, transparent 100%)'
                          : 'linear-gradient(90deg, transparent 0%, rgba(226, 232, 240, 0.8) 50%, transparent 100%)',
                    },
                  },
                },
                '& .MuiTableBody-root': {
                  '& .MuiTableRow-root': {
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'rgba(59, 130, 246, 0.08)'
                          : 'rgba(59, 130, 246, 0.04)',
                      transform: 'scale(1.001)',
                      '& .MuiTableCell-root': {
                        borderBottomColor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(59, 130, 246, 0.15)'
                            : 'rgba(59, 130, 246, 0.1)',
                      },
                    },
                    '&:last-child .MuiTableCell-root': {
                      borderBottom: 'none',
                    },
                  },
                  '& .MuiTableCell-root': {
                    fontSize: '0.875rem',
                    py: 2.5,
                    px: 3,
                    color: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(226, 232, 240, 0.95)'
                        : 'rgba(30, 41, 59, 0.95)',
                    borderBottom: (theme) =>
                      theme.palette.mode === 'dark'
                        ? '1px solid rgba(148, 163, 184, 0.08)'
                        : '1px solid rgba(226, 232, 240, 0.6)',
                    transition: 'all 0.2s ease',
                    '&:first-of-type': {
                      pl: 4,
                    },
                    '&:last-of-type': {
                      pr: 4,
                    },
                  },
                },
              }}
            >
              <TableHead
                // This should not render if useDefaultTable = false
                orderDirection={filter?.orderDirection}
                orderBy={filter?.orderBy}
                onRequestSort={onRequestSort}
                headCells={headCells}
              />
              <TableBody
                style={
                  useCollapseAll ? { display: isAllCollapse ? 'none' : 'table-row-group' } : {}
                }
                className="default-table-body"
              >
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={headCells.length || 1} sx={{ border: 'none', p: 0 }}>
                      <TableLoadingState message="Loading data..." />
                    </TableCell>
                  </TableRow>
                ) : (
                  renderRows()
                )}
              </TableBody>
            </Table>
          </MuiTableContainer>
        ) : renderTable ? (
          renderTable()
        ) : null}
        {/* Pagination */}

        {hidePagination ? null : renderCustomPagination ? (
          renderCustomPagination()
        ) : (
          <TablePagination
            sx={{
              display: 'flex',
              minHeight: '64px',
              px: 4,
              py: 2,
              borderTop: (theme) =>
                theme.palette.mode === 'dark'
                  ? '1px solid rgba(148, 163, 184, 0.12)'
                  : '1px solid rgba(226, 232, 240, 0.6)',
              background: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(0deg, rgba(30, 41, 59, 0.4) 0%, transparent 100%)'
                  : 'linear-gradient(0deg, rgba(248, 250, 252, 0.6) 0%, transparent 100%)',
              '& .MuiTablePagination-root': {
                color: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(226, 232, 240, 0.9)'
                    : 'rgba(51, 65, 85, 0.9)',
              },
              '& .MuiTablePagination-actions button': {
                color: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(226, 232, 240, 0.7)'
                    : 'rgba(51, 65, 85, 0.7)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(59, 130, 246, 0.15)'
                      : 'rgba(59, 130, 246, 0.1)',
                  color: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(59, 130, 246, 1)'
                      : 'rgba(37, 99, 235, 1)',
                },
                '&.Mui-disabled': {
                  color: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(226, 232, 240, 0.3)'
                      : 'rgba(51, 65, 85, 0.3)',
                },
              },
            }}
            rowsPerPageOptions={rowsPerPageOptions as Array<number>}
            component="div"
            count={count as number}
            rowsPerPage={rowsPerPage as number}
            page={page as number}
            onPageChange={onPageChange as any}
            onRowsPerPageChange={onRowsPerPageChange as any}
          />
        )}
      </Paper>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  overflow-y: auto;
  max-width: calc(100vw - ${(props) => props.theme.spacing(12)});
  background-color: transparent;
`;

function TableContainer(props: TableProps | any) {
  return (
    <Grid container spacing={6}>
      <Grid size={12}>
        <EnhancedTable {...props} />
      </Grid>
    </Grid>
  );
}

export default TableContainer;
